import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, LayoutDashboard, LogOut, Users, Settings, Tags, LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

interface MenuItem {
  id: string;
  title: string;
  icon: string;
}

interface DashboardSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  menuItems: MenuItem[];
  onLogout: () => void;
  onHomeClick: () => void;
}

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  Users,
  Settings,
  Tags,
};

export const DashboardSidebar = ({
  isCollapsed,
  setIsCollapsed,
  activeTab,
  onTabChange,
  menuItems,
  onLogout,
  onHomeClick,
}: DashboardSidebarProps) => {
  return (
    <motion.div
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className={cn(
        "h-screen bg-gradient-to-b from-background to-muted/20 border-l flex flex-col transition-all duration-300",
        isCollapsed ? "w-[70px]" : "w-[240px]"
      )}
    >
      <div className="p-4 h-[60px] flex items-center justify-between">
        {!isCollapsed && (
          <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-lg font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent"
          >
            لوحة التحكم
          </motion.h2>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hover:bg-primary/10"
        >
          {isCollapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      <Separator className="mb-4" />

      <nav className="flex-1 p-2 space-y-1">
        {menuItems.map((item, index) => {
          const Icon = iconMap[item.icon];
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Button
                variant={activeTab === item.id ? "secondary" : "ghost"}
                className={cn(
                  "w-full justify-start mb-1 transition-all duration-200",
                  isCollapsed ? "px-2" : "px-4",
                  activeTab === item.id && "bg-primary/10 hover:bg-primary/20"
                )}
                onClick={() => onTabChange(item.id)}
              >
                {Icon && (
                  <Icon 
                    className={cn(
                      "h-5 w-5 transition-colors",
                      activeTab === item.id ? "text-primary" : "text-muted-foreground"
                    )} 
                  />
                )}
                {!isCollapsed && <span className="mr-2">{item.title}</span>}
              </Button>
            </motion.div>
          );
        })}
      </nav>

      <Separator className="my-4" />

      <div className="p-2 space-y-2">
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start transition-all duration-200",
            isCollapsed ? "px-2" : "px-4"
          )}
          onClick={onHomeClick}
        >
          <LayoutDashboard className="h-5 w-5" />
          {!isCollapsed && <span className="mr-2">الرئيسية</span>}
        </Button>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start text-destructive hover:text-destructive transition-all duration-200",
            isCollapsed ? "px-2" : "px-4"
          )}
          onClick={onLogout}
        >
          <LogOut className="h-5 w-5" />
          {!isCollapsed && <span className="mr-2">تسجيل خروج</span>}
        </Button>
      </div>
    </motion.div>
  );
};